import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, Calculator, HeartPulse, Truck, Scale, Users, Briefcase, MessageSquare, Target, Puzzle } from 'lucide-react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { useClientIntakeModal } from '../components/ModalProvider';
import SEO from '../components/SEO';
import { jobListings, industries, whyChooseUs } from '../mockData';

const Home = () => {
  const { openClientIntake } = useClientIntakeModal();

  return (
    <div className="min-h-screen bg-white">
      <SEO 
        title="Premier Recruiting & Staffing Agency"
        description="Omnia Paratus Talent Solutions is a premier Massachusetts recruiting agency specializing in direct hire recruiting for accounting, healthcare, logistics, and HR & people compliance industries. Serving Boston and nationwide."
        keywords="recruiting agency, staffing firm, direct hire recruiting, Massachusetts recruiting agency, Boston staffing agency, accounting recruiters, healthcare recruiters, logistics recruiters, HR recruiters, people compliance recruiters, nationwide recruiting firm, talent acquisition"
      />
      <Header />
      
      {/* Hero Section */}
      <section data-testid="hero-section" className="relative pt-24 pb-24 px-4 sm:px-6 lg:px-8 overflow-hidden min-h-[90vh] flex items-center justify-center">
        {/* Background */}
        <div className="absolute inset-0 z-0 bg-[#0A1F44]">
          <img
            src="https://images.unsplash.com/photo-1758518731468-98e90ffd7430?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2MzR8MHwxfHNlYXJjaHwxfHxwcm9mZXNzaW9uYWwlMjBidXNpbmVzcyUyMHRlYW18ZW58MHx8fHwxNzcxMTIxNDcxfDA&ixlib=rb-4.1.0&q=85"
            alt="Professional Team"
            className="w-full h-full object-cover opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0A1F44] via-[#0A1F44]/90 to-[#0A1F44]"></div>
        </div>

        {/* Subtle gold glow */}
        <div className="absolute inset-0 z-[1] pointer-events-none">
          <div className="absolute top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-[#D4AF37]/8 rounded-full filter blur-[120px]"></div>
        </div>

        <div className="relative z-10 max-w-4xl mx-auto text-center px-4">
          {/* Centered Logo */}
          <div data-testid="hero-logo" className="mb-6">
            <img
              src="https://customer-assets.emergentagent.com/job_talent-delivery-2/artifacts/n21hqg7x_1000003920%20%281%29.png"
              alt="Omnia Paratus Talent Solutions"
              className="mx-auto w-full max-w-[20rem] sm:max-w-[28rem] lg:max-w-[40rem] h-auto drop-shadow-[0_0_40px_rgba(212,175,55,0.15)]"
            />
          </div>

          {/* Divider line */}
          <div className="w-full h-[1px] bg-[#D4AF37]/50 mx-auto mb-10 sm:mb-16"></div>

          {/* Headline */}
          <h1 data-testid="hero-headline" className="text-2xl sm:text-3xl lg:text-4xl font-bold leading-tight tracking-tight mb-6 sm:mb-8">
            <span className="text-white">Powering Talent.</span>{' '}
            <span className="text-[#D4AF37]">Driving Results.</span>
          </h1>

          {/* Supporting copy */}
          <p className="text-base sm:text-lg text-gray-300 mb-8 sm:mb-12 max-w-xl mx-auto leading-relaxed px-2">
            Strategic recruiting solutions connecting exceptional talent with forward-thinking organizations.
          </p>

          {/* CTAs */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button
              size="lg"
              className="bg-[#D4AF37] text-[#0A1F44] hover:bg-[#c9a432] text-lg px-10 py-6 shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-105 font-semibold"
              onClick={openClientIntake}
              data-testid="hero-cta-hire"
            >
              <span className="flex items-center gap-2">
                Request Talent <ArrowRight className="h-5 w-5" />
              </span>
            </Button>
            <Button
              asChild
              size="lg"
              variant="outline"
              className="border-white/30 text-white hover:bg-white hover:text-[#0A1F44] text-lg px-10 py-6 transition-all duration-300"
            >
              <Link to="/candidates" data-testid="hero-cta-jobs">Find Jobs</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* About Snapshot */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[#0A1F44] mb-6">
              Where Experience Meets Results
            </h2>
            <p className="text-lg text-gray-600 leading-relaxed">
              We combine deep industry insight, personalized recruiting, and a consultative approach 
              to deliver talent that drives measurable business outcomes.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { number: '500+', label: 'Successful Placements' },
              { number: '98%', label: 'Client Satisfaction' },
              { number: '15+', label: 'Years Combined Experience' },
              { number: '24hrs', label: 'Average Response Time' }
            ].map((stat, index) => (
              <Card key={index} className="border-none shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
                <CardContent className="p-8 text-center">
                  <div className="text-5xl font-bold text-[#D4AF37] mb-2">{stat.number}</div>
                  <div className="text-gray-600 font-medium">{stat.label}</div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Industries Served */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[#0A1F44] mb-4">
              Industries We Serve
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Specialized expertise in four key sectors driving today's economy
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {industries.map((industry) => {
              const IconComponent = 
                industry.icon === 'calculator' ? Calculator :
                industry.icon === 'heart-pulse' ? HeartPulse :
                industry.icon === 'truck' ? Truck : Scale;
              
              return (
                <Card key={industry.id} className="group border-2 border-gray-200 hover:border-[#D4AF37] transition-all duration-300 hover:shadow-xl">
                  <CardContent className="p-8">
                    <div className="flex items-start space-x-4">
                      <div className="flex-shrink-0 w-16 h-16 bg-[#0A1F44] rounded-xl flex items-center justify-center group-hover:bg-[#D4AF37] transition-colors duration-300">
                        <IconComponent className="h-8 w-8 text-white" />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-2xl font-bold text-[#0A1F44] mb-3">{industry.name}</h3>
                        <p className="text-gray-600 mb-4 leading-relaxed">{industry.description}</p>
                        <div className="flex flex-wrap gap-2">
                          {industry.specialties.map((specialty, idx) => (
                            <span key={idx} className="text-xs px-3 py-1 bg-gray-100 text-gray-700 rounded-full">
                              {specialty}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Additional Industries - centered fifth box */}
          <div className="mt-8 flex justify-center">
            <Card data-testid="additional-industries-card" className="group border-2 border-gray-200 hover:border-[#D4AF37] transition-all duration-300 hover:shadow-xl w-full md:w-1/2">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 bg-[#0A1F44] rounded-xl flex items-center justify-center group-hover:bg-[#D4AF37] transition-colors duration-300 mx-auto mb-4">
                  <Puzzle className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-[#0A1F44] mb-3">Additional Industries</h3>
                <p className="text-gray-600 leading-relaxed">We support hiring across a wide range of functions beyond our core specialties. Tell us what you need — we're ready to help!</p>
              </CardContent>
            </Card>
          </div>

          <div className="text-center mt-12">
            <Button asChild size="lg" className="bg-[#0A1F44] hover:bg-[#0A1F44]/90">
              <Link to="/industries" className="flex items-center gap-2">
                Explore All Industries <ArrowRight className="h-5 w-5" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Services Overview — Contingent Direct Hire */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[#0A1F44] mb-4">
              What We Offer
            </h2>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-0 max-w-6xl mx-auto overflow-hidden rounded-2xl shadow-2xl">
            {/* Image Side */}
            <div className="relative min-h-[400px] lg:min-h-[520px]">
              <img
                src="https://images.unsplash.com/photo-1521791136064-7986c2920216?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODh8MHwxfHNlYXJjaHwxfHxidXNpbmVzcyUyMGhhbmRzaGFrZXxlbnwwfHx8fDE3NzExMjE0NzV8MA&ixlib=rb-4.1.0&q=85"
                alt="Business handshake"
                className="absolute inset-0 w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-transparent to-[#0A1F44]/30"></div>
            </div>

            {/* Content Side */}
            <div className="bg-white p-10 lg:p-14 flex flex-col justify-center">
              <div className="inline-block px-4 py-1.5 bg-[#D4AF37]/10 rounded-full mb-5 self-start">
                <span className="text-[#D4AF37] font-semibold text-sm uppercase tracking-wider">Our Core Offering</span>
              </div>
              <h3 data-testid="direct-hire-title" className="text-3xl lg:text-4xl font-bold text-[#0A1F44] mb-4">
                Contingent Direct Hire
              </h3>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                Success-based recruiting delivering exceptional talent with no upfront costs. We align every search with your business goals, culture, and growth plans.
              </p>
              <ul className="space-y-4 mb-10">
                {['No retainers or upfront fees', 'Rigorous candidate screening', '90-day placement guarantee', 'Deep industry expertise'].map((feature, idx) => (
                  <li key={idx} className="flex items-center gap-3 text-gray-700">
                    <CheckCircle className="h-6 w-6 text-[#D4AF37] flex-shrink-0" />
                    <span className="text-lg">{feature}</span>
                  </li>
                ))}
              </ul>
              <Button asChild size="lg" className="bg-[#0A1F44] hover:bg-[#0A1F44]/90 text-white self-start">
                <Link to="/services" className="flex items-center gap-2">
                  Learn More <ArrowRight className="h-5 w-5" />
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[#0A1F44] mb-4">
              Why Choose Us?
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {whyChooseUs.map((item) => {
              const iconMap = {
                'users': Users,
                'briefcase': Briefcase,
                'message-square': MessageSquare,
                'target': Target
              };
              const IconComponent = iconMap[item.icon];
              
              return (
                <div key={item.id} className="text-center group">
                  <div className="w-24 h-24 mx-auto mb-6 rounded-2xl flex items-center justify-center transform group-hover:scale-110 transition-transform duration-300 bg-gradient-to-br from-[#0A1F44] to-[#0d2b5c] p-4">
                    <IconComponent className="w-12 h-12 text-[#D4AF37]" />
                  </div>
                  <h3 className="text-xl font-bold text-[#0A1F44] mb-3">{item.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{item.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-[#0A1F44] to-[#0d2b5c] relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 right-10 w-96 h-96 bg-[#D4AF37] rounded-full filter blur-3xl"></div>
        </div>
        
        <div className="relative max-w-4xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Ready to Transform Your Hiring?
          </h2>
          <p className="text-xl text-gray-200 mb-10">
            Let's discuss how we can help you find the talent that drives your business forward.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button
              asChild
              size="lg"
              className="bg-[#D4AF37] text-[#0A1F44] hover:bg-[#D4AF37]/90 text-lg px-8 py-6 shadow-xl hover:shadow-2xl transition-all duration-300"
            >
              <Link to="/contact" className="flex items-center gap-2">
                Get Started <ArrowRight className="h-5 w-5" />
              </Link>
            </Button>
            <Button
              asChild
              size="lg"
              variant="outline"
              className="bg-white/10 backdrop-blur-sm border-white/30 text-white hover:bg-white hover:text-[#0A1F44] text-lg px-8 py-6"
            >
              <Link to="/candidates">Browse Jobs</Link>
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Home;
